import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { isNativeBackupAvailable, getNativeBackupStatus } from '../utils/nativeBackup';

export default function UnlockModal({ onLogout, contextNote }) {
  const { t } = useTranslation('auth');
  const { user, unlockWithPassword, pending2fa, pending2faError, submit2fa, cancel2fa } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  // 2FA code boxes
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const codeRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  // LO STATO DEL BACKUP SI CHIEDE AL PLUGIN, NON ALLA SESSIONE (2026-09-12).
  //
  // Riaprendo l'app si finisce qui, e la scheda che direbbe "il backup e'
  // attivo" sta nel profilo, cioe' dietro questa schermata: chi legge
  // "Sessione bloccata" pensa che sia tutto fermo, mentre il servizio sta
  // caricando le sue foto in quel momento (misurato: 1.255 file caricati
  // con la WebView ferma). La verita' ce l'ha il Keystore, e si chiede a lui.
  const [backupAttivo, setBackupAttivo] = useState(false);
  useEffect(() => {
    if (!isNativeBackupAvailable()) return;
    getNativeBackupStatus().then((s) => setBackupAttivo(!!s?.enabled)).catch(() => {});
  }, []);

  // "Non sei tu?" vuol dire che al telefono c'e' un'altra persona: la chiave
  // del proprietario precedente se ne deve andare, ed e' giusto che se ne
  // vada. Ma chi preme deve sapere cosa sta spegnendo — il bottone diceva
  // solo "Esci", e spegneva anche il backup di questo dispositivo.
  function escoDavvero() {
    if (backupAttivo && !window.confirm(t('unlock_logout_conferma'))) return;
    onLogout();
  }

  async function handleUnlock(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await unlockWithPassword(password);
    } catch (err) {
      setError(err.message?.includes('credentials') ? t('unlock_err_wrong_password') : (err.message || t('unlock_err_generic')));
      setPassword('');
    } finally {
      setLoading(false);
    }
  }

  async function handle2fa(e) {
    e.preventDefault();
    setError('');
    const fullCode = code.join('');
    if (fullCode.length !== 6) return setError(t('unlock_err_enter_all_digits'));
    setLoading(true);
    try { await submit2fa(fullCode); }
    catch { /* error shown via pending2faError */ }
    finally { setLoading(false); }
  }

  function handleCodeInput(i, val) {
    const digit = val.replace(/\D/g, '').slice(-1);
    const next  = [...code]; next[i] = digit; setCode(next);
    if (digit && i < 5) codeRefs[i + 1].current?.focus();
  }

  function handleCodeKey(i, e) {
    if (e.key === 'Backspace' && !code[i] && i > 0) codeRefs[i - 1].current?.focus();
  }

  function handleCodePaste(e) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) { setCode(pasted.split('')); codeRefs[5].current?.focus(); }
  }

  // ── 2FA screen ─────────────────────────────────────────
  if (pending2fa) {
    return (
      <div className="unlock-overlay">
        <div className="unlock-card">
          <div className="unlock-icon">🔑</div>
          <h2>{t('unlock_twofa_title')}</h2>
          <p className="unlock-sub">
            {t('unlock_twofa_subtitle')}
          </p>

          <form onSubmit={handle2fa} className="auth-form">
            <div className="code-inputs" onPaste={handleCodePaste}>
              {code.map((digit, i) => (
                <input
                  key={i} ref={codeRefs[i]}
                  type="text" inputMode="numeric" maxLength={1}
                  value={digit}
                  onChange={e => handleCodeInput(i, e.target.value)}
                  onKeyDown={e => handleCodeKey(i, e)}
                  className="code-box" autoFocus={i === 0}
                />
              ))}
            </div>
            {(pending2faError || error) && <div className="auth-error">⚠ {pending2faError || error}</div>}
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? t('unlock_twofa_verify_loading') : t('unlock_twofa_verify_btn')}
            </button>
          </form>

          <button className="unlock-logout" onClick={cancel2fa}>{t('unlock_twofa_back_btn')}</button>
        </div>
      </div>
    );
  }

  // ── Password screen ────────────────────────────────────
  return (
    <div className="unlock-overlay">
      <div className="unlock-card">
        <div className="unlock-icon">🔐</div>
        <h2>{t('unlock_title')}</h2>
        {contextNote ? (
          // Caller-supplied reason this unlock is happening right now (e.g.
          // PaymentSuccessPage — "your payment went through, this is why you
          // need your password again") — replaces the generic welcome-back
          // copy so it doesn't read as an unrelated, unexplained interruption.
          <p className="unlock-sub">{contextNote}</p>
        ) : (
          <p className="unlock-sub">
            {t('unlock_welcome_back')}, <strong>@{user?.username}</strong>.<br/>
            {t('unlock_reenter_password')}
          </p>
        )}

        <form onSubmit={handleUnlock} className="auth-form">
          <div className="field">
            <label>{t('field_password_label')}</label>
            <input
              type="password" autoFocus
              value={password} onChange={e => setPassword(e.target.value)}
              placeholder={t('field_password_placeholder')} required autoComplete="current-password"
            />
          </div>
          {error && <div className="auth-error">⚠ {error}</div>}
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? t('unlock_loading') : t('unlock_btn')}
          </button>
        </form>

        {backupAttivo && (
          <p className="unlock-backup-attivo">📸 {t('unlock_backup_attivo')}</p>
        )}

        <button className="unlock-logout" onClick={escoDavvero}>
          {backupAttivo ? t('unlock_logout_btn_backup') : t('unlock_logout_btn')}
        </button>
      </div>
    </div>
  );
}
